import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from './agent.model';
import type { ActionTrackingContext } from '../actions/framework/action-tracking';

type Params = {
  model: string;
};

export class ChangeAgentModelAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Change Agent Model';
  description = 'Change the LLM model for the agent chat';
  inputSchema = z.object({
    model: z.string(),
  });
  discrete = true;

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AgentModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agent = context.targetedModelOfType(AgentModel);

    if (agent === null) {
      return ok({});
    }

    agent.formState.set({
      ...agent.formState.snapshot(),
      model: params.model,
    });

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    return {
      event: 'agent_model_changed',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        toModel: payload.model,
        toolCount: agent.promptInput.snapshot().toolIds.length,
        isNew: agent.isNew,
      },
    };
  }
}
